import { zodResolver } from "@hookform/resolvers/zod";
import {
  BMButton,
  BMInputField,
  BMPopup,
  BMText,
} from "@traveloka/district-bloom";
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext,
} from "react-hook-form";
import { z } from "zod";
import BMView from "../../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../../core/resource/MobileResourceProvider";
import { FlightPrebookFormValue } from "../../types";

type Props = {
  visible?: boolean;
  isLoading?: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
};

export default function ModalCompliantConfirmation({
  visible = true,
  isLoading = false,
  onClose = () => {},
  onConfirm = () => {},
}: Props) {
  const { CorporateFlightPrebookForm: cr, CorporateCommon: crm } =
    useMobileContentResource();
  const { setValue } = useFormContext<FlightPrebookFormValue>();

  const compliantFormSchema = z.object({
    policyBypassRemarks: z
      .string({ message: cr.bookingNonCompliantInputRequiredError })
      .min(1, cr.bookingNonCompliantInputRequiredError)
      .max(100, { message: cr.bookingNonCompliantInputMaxCharError }),
  });

  const method = useForm<{ policyBypassRemarks: string }>({
    resolver: zodResolver(compliantFormSchema),
  });
  const { control, handleSubmit } = method;

  function handleConfirm(data: { policyBypassRemarks: string }) {
    setValue("policyBypassRemarks", data.policyBypassRemarks);
    onConfirm();
  }

  return (
    <BMPopup
      dismissible={false}
      isVisible={visible}
      onPopupClose={onClose}
      title={cr.bookingNonCompliantModalTitle}
      withCloseButton
    >
      <BMView spacing="lg">
        <BMText variant="paragraphNormal">
          {cr.bookingNonCompliantModalDescription}
        </BMText>
        <FormProvider {...method}>
          <BMView spacing="lg">
            <Controller
              control={control}
              name="policyBypassRemarks"
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => {
                return (
                  <BMInputField
                    value={value || ""}
                    onChange={onChange}
                    error={error?.message}
                    placeholder={cr.bookingNonCompliantInputPlaceholder}
                  />
                );
              }}
            />
            <BMView spacing="sm" row>
              <BMView flex>
                <BMButton
                  size="LARGE"
                  role="SECONDARY"
                  stretchable
                  onPress={onClose}
                  label={crm.goBackText}
                />
              </BMView>
              <BMView flex>
                <BMButton
                  size="LARGE"
                  stretchable
                  loading={isLoading}
                  label={crm.continueText}
                  onPress={handleSubmit(handleConfirm)}
                />
              </BMView>
            </BMView>
          </BMView>
        </FormProvider>
      </BMView>
    </BMPopup>
  );
}
